import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Paper,
  FormHelperText,
  alpha,
} from '@mui/material';
import {
  RISK_MATRIX_TYPES,
  RiskMatrixType,
  RISK_MATRIX_TYPE_LABELS,
  RISK_LEVEL_LABELS,
  RISK_LEVEL_COLORS,
  calculateRiskLevel,
  getProbabilityLabels,
  getSeverityLabels,
  getMaxProbability,
  getMaxSeverity,
} from '../../../../types/riskMatrix';
import { RiskMatrixGrid } from './RiskMatrixGrid';

/** Os ids dos três campos, para quem precisa levar o foco a um deles. */
export interface IdsDosCamposDaMatriz {
  tipo: string;
  probabilidade: string;
  severidade: string;
}

const IDS_PADRAO: IdsDosCamposDaMatriz = {
  tipo: 'matriz-de-risco-tipo',
  probabilidade: 'matriz-de-risco-probabilidade',
  severidade: 'matriz-de-risco-severidade',
};

interface RiskMatrixStepProps {
  disabled?: boolean;
  ids?: IdsDosCamposDaMatriz;
}

const idDoRotulo = (idDoCampo: string): string => `${idDoCampo}-rotulo`;

export const RiskMatrixStep: React.FC<RiskMatrixStepProps> = ({ disabled = false, ids = IDS_PADRAO }) => {
  const { control, watch, setValue, formState: { errors } } = useFormContext();

  const matrixType = watch('riskMatrixType') as RiskMatrixType | null;
  const probability = watch('riskProbability') as number | null;
  const severity = watch('riskSeverity') as number | null;

  const probabilityError = errors.riskProbability?.message as string | undefined;
  const severityError = errors.riskSeverity?.message as string | undefined;

  const riskLevel =
    matrixType && probability && severity
      ? calculateRiskLevel(matrixType, probability, severity)
      : null;

  const handleMatrixTypeChange = (newType: RiskMatrixType | '') => {
    if (!newType) {
      setValue('riskMatrixType', null);
      setValue('riskProbability', null);
      setValue('riskSeverity', null);
      return;
    }
    setValue('riskMatrixType', newType);
    const maxProb = getMaxProbability(newType);
    const maxSev = getMaxSeverity(newType);
    if (probability && probability > maxProb) setValue('riskProbability', null);
    if (severity && severity > maxSev) setValue('riskSeverity', null);
  };

  const probLabels = matrixType ? getProbabilityLabels(matrixType) : {};
  const sevLabels = matrixType ? getSeverityLabels(matrixType) : {};
  const maxProb = matrixType ? getMaxProbability(matrixType) : 0;
  const maxSev = matrixType ? getMaxSeverity(matrixType) : 0;

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth disabled={disabled}>
          <InputLabel id={idDoRotulo(ids.tipo)}>Tipo de Matriz</InputLabel>
          <Controller
            name="riskMatrixType"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? ''}
                label="Tipo de Matriz"
                labelId={idDoRotulo(ids.tipo)}
                SelectDisplayProps={{ id: ids.tipo }}
                onChange={(e) => handleMatrixTypeChange(e.target.value as RiskMatrixType | '')}
              >
                <MenuItem value=""><em>Nenhuma</em></MenuItem>
                {RISK_MATRIX_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {RISK_MATRIX_TYPE_LABELS[type]}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        {riskLevel && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              height: '100%',
              bgcolor: alpha(RISK_LEVEL_COLORS[riskLevel], 0.08),
              borderColor: RISK_LEVEL_COLORS[riskLevel],
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Nível de Risco:
            </Typography>
            <Chip
              label={RISK_LEVEL_LABELS[riskLevel]}
              sx={{
                fontWeight: 700,
                bgcolor: RISK_LEVEL_COLORS[riskLevel],
                color: '#fff',
              }}
            />
          </Paper>
        )}
      </Grid>

      {matrixType && (
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth error={!!probabilityError} disabled={disabled}>
            <InputLabel id={idDoRotulo(ids.probabilidade)}>Probabilidade</InputLabel>
            <Controller
              name="riskProbability"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? ''}
                  label="Probabilidade"
                  labelId={idDoRotulo(ids.probabilidade)}
                  SelectDisplayProps={{ id: ids.probabilidade }}
                >
                  <MenuItem value=""><em>Selecione</em></MenuItem>
                  {Array.from({ length: maxProb }, (_, i) => i + 1).map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} — {probLabels[n]}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {probabilityError && <FormHelperText>{probabilityError}</FormHelperText>}
          </FormControl>
        </Grid>
      )}

      {matrixType && (
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth error={!!severityError} disabled={disabled}>
            <InputLabel id={idDoRotulo(ids.severidade)}>Severidade</InputLabel>
            <Controller
              name="riskSeverity"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? ''}
                  label="Severidade"
                  labelId={idDoRotulo(ids.severidade)}
                  SelectDisplayProps={{ id: ids.severidade }}
                >
                  <MenuItem value=""><em>Selecione</em></MenuItem>
                  {Array.from({ length: maxSev }, (_, i) => i + 1).map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} — {sevLabels[n]}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {severityError && <FormHelperText>{severityError}</FormHelperText>}
          </FormControl>
        </Grid>
      )}

      {matrixType && (
        <Grid size={12}>
          <RiskMatrixGrid
            matrixType={matrixType}
            selectedProbability={probability}
            selectedSeverity={severity}
          />
        </Grid>
      )}

      {!matrixType && (
        <Grid size={12}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Selecione um tipo de matriz para classificar o nível de risco. Este passo é opcional.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};
